/**
 * AI spatial analysis — sends a compact summary of the user's visible
 * vector layers to /ai/analyze (which proxies Anthropic) and renders the
 * markdown response in the right-panel AI slot.
 *
 * The analyst is domain-agnostic; we don't bake in any assumptions about
 * what the data is — the prompt adapts to whatever columns and geometry
 * types the user has loaded.
 */
import { analyzeVisibleLayers } from '../api/ai.js';
import { auth } from '../auth/state.js';
import { openAuthModal } from '../auth/modal.js';
import { toast } from '../ui/toast.js';
import { renderMarkdown } from '../ui/markdown.js';

let userLayersRef = null;
export function registerUserLayers(manager) { userLayersRef = manager; }

const MAX_SAMPLE_PER_COL = 6;
const MAX_COLUMNS_PER_LAYER = 12;

export async function runAIAnalysis() {
  if (!auth.isAuthenticated()) { openAuthModal(); return; }

  const visible = userLayersRef?.getVisibleVectorLayersWithFeatures?.() || [];
  if (visible.length === 0) {
    toast('No visible vector layers — add one to the map first.');
    return;
  }

  const panel  = ensurePanel();
  const output = panel.querySelector('.ai-output');
  output.innerHTML = `
    <div style="font-style:italic;color:var(--text3);font-size:12px;padding:6px 0">
      Analysing ${visible.length} layer${visible.length === 1 ? '' : 's'}…
    </div>
  `;
  panel.style.display = 'block';

  const layers = visible.map(summarize);

  try {
    const resp = await analyzeVisibleLayers({ layers });
    output.innerHTML = renderMarkdown(resp.markdown || '_No response._');
    if (resp.model) {
      output.insertAdjacentHTML(
        'beforeend',
        `<div style="margin-top:10px;font-family:var(--mono);font-size:9.5px;color:var(--text3);letter-spacing:0.06em">via ${escape(resp.model)}</div>`,
      );
    }
    toast('AI analysis ready');
  } catch (e) {
    output.innerHTML = `
      <div style="color:var(--red);font-size:12px;padding:6px 0;line-height:1.5">
        ${escape(e.message)}
      </div>
    `;
  }
}

/** Compact, prompt-friendly summary of one layer. */
function summarize(layer) {
  const features = layer.features || [];
  const props    = features[0]?.properties || {};
  const columns  = Object.keys(props).slice(0, MAX_COLUMNS_PER_LAYER);
  const sample_values = {};

  for (const col of columns) {
    const seen = new Set();
    for (const f of features) {
      const v = f.properties?.[col];
      if (v == null || v === '') continue;
      const tidy = typeof v === 'number'
        ? Math.round(v * 100) / 100
        : String(v).slice(0, 40);
      seen.add(tidy);
      if (seen.size >= MAX_SAMPLE_PER_COL) break;
    }
    sample_values[col] = [...seen];
  }

  return {
    name:           layer.name,
    geometry_type:  layer.geometryType,
    feature_count:  features.length,
    columns,
    sample_values,
  };
}

function ensurePanel() {
  let panel = document.getElementById('aiPanel');
  if (panel) return panel;
  const host = document.getElementById('rp-national') || document.getElementById('rpBody');
  if (!host) throw new Error('No right panel host to mount AI output into');

  panel = document.createElement('div');
  panel.id = 'aiPanel';
  panel.className = 'ai-panel';
  panel.style.display = 'none';
  panel.innerHTML = `
    <h4 style="display:flex;align-items:center;gap:6px;font-size:13px;font-weight:600;margin-bottom:10px">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/></svg>
      AI analysis
      <span class="ai-badge" style="font-size:9.5px;padding:1px 6px;border-radius:999px;background:var(--bg4);color:var(--text2);letter-spacing:0.04em;font-family:var(--mono);font-weight:500">Claude</span>
    </h4>
    <div class="ai-output st-md" style="font-size:13px;line-height:1.6"></div>
  `;
  host.insertBefore(panel, host.firstChild);
  return panel;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
